<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import { FormLabel, FormInput } from "../../../../base-components/Form";
import Button from "../../../../base-components/Button";
import { computed, reactive, ref } from "vue";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import { usePedidosStore } from "../../../../services/pedidos";
import {
  formatearFechaTimestampDDMMYYYY,
  getCurrentTime
} from "../../../../utils/fecha";
import FormSelect from "../../../../base-components/Form/FormSelect.vue";
import { useMensajesStore } from "../../../../services/mensajes";
import { ESTADOS_PEDIDO } from "../../../../constants/estadosPedido";
import { LogAcciones, Pedido } from "@fixeduy/restaurantestypes";
import { userUserStore } from "../../../../stores/user";
import { useLogAccionesStore } from "../../../../services/logAcciones";
import { utilFechaConvertirNumeroString } from "../../../../utils/fechaFixedTypes";

interface Props {
  pedido: Pedido;
}

const userStore = userUserStore();
const logAccionesStore = useLogAccionesStore();

const props = defineProps<Props>();
const sendButtonRef = ref(null);
const cargando = ref(false);
const errorMsg = ref<string>("");
const identificador = computed(() => {
  if (!props.pedido) return "";
  return `${props.pedido.mesaNombre} | ${utilFechaConvertirNumeroString(props.pedido.fechaCreacion, true, "/")}`;
});

const pedidosStore = usePedidosStore();
const mensajesStore = useMensajesStore();

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();

const formData = reactive({
  id: props.pedido.id,
  estado: props.pedido.estado
});

const handleEdit = async () => {
  cargando.value = true;
  errorMsg.value = "";

  const result = await pedidosStore.modificarEstado(
    props.pedido.id,
    formData.estado
  );

  cargando.value = false;

  if (props.pedido.estado != formData.estado) {
    const logAcciones: LogAcciones = {
      id: "",
      accion: [
        `Se modifica estado de pedido N°${props.pedido.numero}: ${props.pedido.estado} -> ${formData.estado}`
      ],
      modulo: "pedidos",
      fecha: getCurrentTime(),
      usuario: userStore.userLoggedIn?.uid!,
      mail: userStore.userLoggedIn?.email!
    };

    await logAccionesStore.guardarLog(logAcciones);
  }
  if (result == true) {
    mensajesStore.mostrarToast(
      "Estado modificado con exito",
      identificador.value,
      mensajesStore.mensajeType.exito
    );
    emit("cerrar");
  } else {
    errorMsg.value = String(result);
  }
};
</script>

<template>
  <Dialog
    :open="true"
    @close="
      () => {
        emit('cerrar');
      }
    "
    :initialFocus="sendButtonRef"
    size="lg"
  >
    <Dialog.Panel class="overflow-hidden flex flex-col max-h-[85vh]">
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">
          Modificar estado pedido # {{ props.pedido.numero }}
        </h2>
      </Dialog.Title>
      <!-------------------- DIALOG DESCRIPTION ------------------------>

      <Dialog.Description>
        <!-------------------- Nombre ------------------------>
        <div class="flex">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1 ml-1" class="w-40 sw:w-20">
              Nombre</FormLabel
            >
          </div>
          <div class="flex-1 ml-2">
            <FormInput type="text" disabled :value="identificador" />
          </div>
        </div>
        <!-- Estado -->
        <div class="flex mt-5">
          <div class="flex-none w-50 text-right pr-5 mt-2">
            <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              Estado
            </FormLabel>
          </div>
          <div class="flex-1 ml-2">
            <FormSelect v-model.trim="formData.estado">
              <option
                v-for="opt in ESTADOS_PEDIDO"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </FormSelect>
            <!-- <template v-if="validate$.tipoDocumento?.$error">
              <div
                v-for="(error, index) in validate$.tipoDocumento.$errors"
                :key="index"
                class="mt-2 text-danger"
              >
                {{ error.$message }}
              </div>
            </template> -->
          </div>
        </div>

        <p v-if="errorMsg" class="mt-3 text-sm text-danger" role="alert">
          {{ errorMsg }}
        </p>
      </Dialog.Description>

      <!-------------------- DIALOG FOOTER ------------------------>
      <Dialog.Footer>
        <div v-if="!cargando" class="flex text-right">
          <!-------------------- Botón Cancelar ------------------------>
          <div class="flex-1 text-left">
            <Button
              variant="secondary"
              class="mb-2 mr-1"
              @click="() => emit('cerrar')"
            >
              Cancelar
            </Button>
          </div>
          <!-------------------- Botón Guardar ------------------------>
          <div class="flex-1">
            <Button variant="primary" class="mb-2 mr-1" @click="handleEdit">
              Guardar
            </Button>
          </div>
        </div>
        <div v-else class="flex justify-center items-center">
          <div class="flex flex-col items-center">
            <LoadingIcon icon="oval" class="w-8 h-8" />
            <span class="mt-2 text-sm text-slate-600">Cargando...</span>
          </div>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
